import { SupplierUserRole } from '@choco-admin/types';
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Button,
} from '@heroui/react';
import React from 'react';

/** Human-readable labels for the supplier/distributor user roles. */
export const SUPPLIER_ROLE_LABELS: Record<SupplierUserRole, string> = {
  [SupplierUserRole.ADMIN]: 'Admin',
  [SupplierUserRole.SALES_REP]: 'Sales Rep',
};

interface SupplierRoleSelectProps {
  /** Currently selected role. When undefined, a placeholder is shown. */
  value?: SupplierUserRole | null;
  onChange: (role: SupplierUserRole) => void;
  disabled?: boolean;
}

/**
 * Single-select dropdown for a user's supplier role. Mirrors the buyer-side
 * RoleSelect, but is page-agnostic (used by both the user profile's linked
 * distributor section and the Suppliers page Team tab).
 */
export const SupplierRoleSelect: React.FC<SupplierRoleSelectProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const roleItems = [
    {
      key: SupplierUserRole.ADMIN,
      label: SUPPLIER_ROLE_LABELS[SupplierUserRole.ADMIN],
    },
    {
      key: SupplierUserRole.SALES_REP,
      label: SUPPLIER_ROLE_LABELS[SupplierUserRole.SALES_REP],
    },
  ];

  const label = value ? SUPPLIER_ROLE_LABELS[value] : 'Select role...';

  return (
    <Dropdown>
      <DropdownTrigger>
        <Button
          variant="bordered"
          className="justify-between min-w-[140px] cursor-pointer hover:bg-default-100 hover:border-default-300 transition-all duration-200 data-[hover=true]:bg-default-100 data-[hover=true]:border-default-300"
          isDisabled={disabled}
          size="sm"
          endContent={
            <svg
              className="w-4 h-4 text-default-400 transition-transform duration-200 group-data-[open=true]:rotate-180"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          }
        >
          <span className="text-sm font-medium text-foreground">{label}</span>
        </Button>
      </DropdownTrigger>
      <DropdownMenu
        aria-label="Supplier role selection"
        onAction={(key) => onChange(key as SupplierUserRole)}
        selectedKeys={value ? [value] : []}
        selectionMode="single"
        className="min-w-[140px]"
        itemClasses={{
          base: 'cursor-pointer hover:bg-default-100 data-[hover=true]:bg-default-100 transition-colors duration-150',
          title: 'text-sm font-medium',
        }}
      >
        {roleItems.map((item) => (
          <DropdownItem key={item.key}>
            <span className="text-sm">{item.label}</span>
          </DropdownItem>
        ))}
      </DropdownMenu>
    </Dropdown>
  );
};
